/*  MUI  */
import { Add, PlayDisabled } from '@mui/icons-material';
import {
    Box,
    Button,
    FormLabel,
    Grid,
    Paper,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { DateFormat } from 'application/Shared/Components/Core';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip, TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import {
    Helpers,
    Notify,
    Response,
    Transition
} from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';
import moment from 'moment-timezone';

/*  Objects  */
import { User } from 'objects/User';

/*  React  */
import React, { Component, useState } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { withRouter } from 'router/withRouter';

import { EditTenantModal } from './Components/EditTenantModal';

/*  Constants  */
const { getData, hasData } = Response;

const csvFields = [
    { key: 'id', label: i18n.t('Common.id') },
    { key: 'region', label: i18n.t('Common.region') },
    {
        key: 'tenantStats.totalClients',
        label: i18n.t('Common.client_count')
    },
    {
        key: 'tenantStats.totalPartnerUsers',
        label: i18n.t('Common.user_count')
    },
    {
        key: 'tenantStats.totalPartnerDevices',
        label: i18n.t('Common.device_count')
    },
    { key: 'name', label: i18n.t('Common.name') },
    { key: 'adminUser.email', label: i18n.t('CSV.admin_user_email') },
    {
        key: 'adminUser.firstName',
        label: i18n.t('CSV.admin_user_first_name')
    },
    { key: 'adminUser.lastName', label: i18n.t('CSV.admin_user_last_name') },
    {
        key: 'dateCreated',
        label: i18n.t('Common.dateAdded')
    },
    {
        key: 'settings.trialEndDate',
        label: i18n.t('GlobalAdmin.Tenant.TrialEndDate.label')
    },
    { key: 'anm', label: i18n.t('Common.anm'), transform: 'bool_YN' }
];

const headers = [
    {
        renderHeader: i18n.t('Common.name'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'name',
        width: '30%'
    },
    {
        renderHeader: i18n.t('Common.region'),
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'region',
        width: '5%'
    },
    {
        renderHeader: i18n.t('Common.client_count'),
        width: '5%'
    },
    {
        renderHeader: i18n.t('Common.user_count'),
        width: '5%'
    },
    {
        renderHeader: i18n.t('Common.device_count'),
        width: '5%'
    },
    {
        renderHeader: i18n.t('Common.dateAdded'),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'dateCreated',
        width: '10%'
    },
    {
        renderHeader: i18n.t('GlobalAdmin.Tenant.TrialEndDate.label'),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'settings.trialEndDate',
        width: '10%'
    },
    {
        renderHeader: i18n.t('Common.admin_user'),
        width: '10%'
    },
    {
        renderHeader: i18n.t('Common.partner'),
        width: '10%'
    },
    {
        align: 'right',
        renderHeader: i18n.t('Common.actions'),
        width: '10%'
    }
];

const parameters = {
    global: true,
    includeAdmin: true,
    includePartnerDetails: true,
    includeSettings: true,
    name: '*'
};

const searchFields = ['name', 'id'];

const toggles = [
    {
        defaultEnabled: true,
        parameter: {
            field: 'partner',
            value: 'true'
        },
        toggleText: i18n.t('GlobalAdmin.Tenant.partnersOnly')
    }
];

/*  State  */
const state = () => ({
    adding: false,
    adminEmail: '',
    csvData: {},
    loaded: false,
    partnerName: '',
    rerender: false,
    tenantStats: {}
});

const Actions = ({ tenant, rerenderFn }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const options = [
        {
            key: 'GlobalAdmin.Tenant.EditTenant',
            label: t('GlobalAdmin.Tenant.EditTenant'),
            value: 'edit'
        }
    ];

    const onClick = (value) => {
        if (value === 'edit') {
            setOpen(true);
        }
    };

    return (
        <>
            <KebabMenu
                options={options}
                onClick={onClick}
                paperProps={{ sx: { width: 240 } }}
            />
            <EditTenantModal
                tenant={tenant}
                open={open}
                onCloseFn={() => {
                    setOpen(false);
                }}
                rerenderFn={rerenderFn}
            />
        </>
    );
};

class GlobalTenant extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = () => {
        this.loadTenantStats();
        this.setState({ loaded: true });
    };

    unload = () => {
        this.requests.cancel();
    };

    loadTenantStats = async () => {
        let data;

        // cron job runs on EST
        const date = moment(new Date())
            .tz('America/Toronto')
            .format('YYYY-MM-DD');

        const params = {
            day: date,
            key: `[totalClients,totalPartnerUsers,totalPartnerDevices]`,
            size: -1
        };

        try {
            const response = await axios.get('/tenantStat', {
                cancelToken: this.requests.token(),
                params
            });
            if (!hasData(response)) {
                return;
            }
            data = getData(response);
        } catch (e) {
            return;
        }

        const tenantStatsMap = data.reduce(
            (acc, { platformTenantId, key, value }) => {
                const tenantStats = acc[platformTenantId] || {
                    totalClients: 0,
                    totalPartnerDevices: 0,
                    totalPartnerUsers: 0
                };
                tenantStats[key] = value;
                return { ...acc, [platformTenantId]: tenantStats };
            },
            {}
        );

        if (!this.unmount) {
            this.setState({ tenantStats: tenantStatsMap });
        }
    };

    rerender = async () => {
        if (this.unmount) {
            return;
        }

        this.setState({ rerender: true }, () =>
            this.setState({ rerender: false })
        );
    };

    createPartner = async () => {
        const { t } = this.props;
        const { adminEmail } = this.state;

        const tenantId = await this.createPartnerTenant();
        if (!tenantId) {
            Notify.e(t('GlobalAdmin.Tenant.unabletoCreatePartner'));
            return;
        }

        try {
            await axios.post('/user', {
                admin: true,
                email: adminEmail,
                firstName: adminEmail,
                lastName: '',
                tenantId,
                tenantUserId: adminEmail
            });
            Notify.s(t('GlobalAdmin.Tenant.partnerCreatedSuccessfully'));
        } catch (e) {
            Notify.e(
                t('GlobalAdmin.Tenant.unabletoCreateAdminUserUnderNewPartner')
            );
            debug.e(e);
        }

        this.rerender();
        this.setState({ adding: false });
    };

    createPartnerTenant = async () => {
        const { partnerName } = this.state;

        try {
            const response = await axios.post('/tenant', {
                identityTenantId: '',
                identityTenantName: partnerName,
                identityTenantType: '',
                name: partnerName,
                partner: true
            });
            if (!Response.hasData(response)) {
                debug.e('No Create Tenant Response', response);
                return false;
            }

            return Response.getId(response);
        } catch (e) {
            debug.e('Create Partner Tenant Error:', e);
            return false;
        }
    };

    renderCreatePartner = () => {
        const { t } = this.props;
        const { adding, partnerName, adminEmail } = this.state;

        if (!adding) {
            return null;
        }

        return (
            <>
                <ModalBody style={{ padding: '32px' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormLabel component="label">
                                {t('Common.partner_name')}
                            </FormLabel>
                            <TextField
                                autoFocus
                                onChange={(e) =>
                                    this.setState({
                                        partnerName: e.target.value
                                    })
                                }
                                fullWidth
                                value={partnerName}
                                placeholder={t('Common.brand_name')}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel component="label">
                                {t('Common.admin_email')}
                            </FormLabel>
                            <TextField
                                fullWidth
                                onChange={(e) =>
                                    this.setState({
                                        adminEmail: e.target.value
                                    })
                                }
                                value={adminEmail}
                                placeholder="support@auvik.com"
                                type="email"
                            />
                        </Grid>
                    </Grid>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={() => this.setState({ adding: false })}
                        color="primary"
                        variant="outlined"
                    >
                        {t('Common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => this.createPartner()}
                        color="primary"
                    >
                        {t('Common.create')}
                    </Button>
                </ModalFooter>
            </>
        );
    };

    handleRows = (rows) => {
        const { tenantStats } = this.state;

        if (!rows?.length) {
            return;
        }

        const csvData = rows.reduce((acc, { id, ...rest }) => {
            const tenantStat = Helpers.isset(tenantStats, id)
                ? tenantStats[id]
                : {};
            return [...acc, { id, ...rest, tenantStats: tenantStat }];
        }, []);

        if (this.unmount) return;
        this.setState({ csvData });
    };

    renderTenantStats = (platformTenantId) => {
        const { tenantStats } = this.state;

        const rowStats = tenantStats[platformTenantId];

        return (
            <>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {rowStats ? rowStats.totalClients : '-'}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {rowStats ? rowStats.totalPartnerUsers : '-'}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {rowStats ? rowStats.totalPartnerDevices : '-'}
                </TableCell>
            </>
        );
    };

    renderTenant = (row) => {
        const { t } = this.props;
        const {
            adminUser,
            anm,
            blocked,
            dateCreated,
            id,
            name,
            partner,
            partnerTenant,
            region,
            settings
        } = row;
        const { brandName, trialEndDate } = settings;

        const hasSupportAccess =
            row.settings?.enableAuvikSupportAccess !== false;

        let tooltipText;

        if (!hasSupportAccess) {
            tooltipText = t('GlobalAdmin.Tenant.revokedRemoteAccess');
        }

        const clientTenantId = partner ? null : row.id;
        const partnerTenantId = partner ? row.id : partnerTenant.id;
        const routeToPartner = `/partner/${partnerTenantId}/dashboard`;
        const routeToClient = clientTenantId
            ? `/partner/${partnerTenantId}/admin/${clientTenantId}`
            : routeToPartner;
        const tenantName = brandName || name;

        return (
            <User data={row}>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    <Grid container>
                        <Grid
                            item
                            xs={12}
                            display="flex"
                            alignItems="flex-start"
                        >
                            {blocked && (
                                <TooltipQ
                                    text={t('Common.activity_blocked')}
                                    content={
                                        <PlayDisabled
                                            sx={{
                                                color: 'icon.primary',
                                                float: 'right',
                                                fontSize: FontSizes[3]
                                            }}
                                        />
                                    }
                                />
                            )}
                            <DarkTooltip arrow title={tooltipText}>
                                <Box>
                                    <Link
                                        className={
                                            hasSupportAccess
                                                ? 'link underline'
                                                : null
                                        }
                                        data-testid={`partnerClientMainLink${tenantName}`}
                                        to={routeToClient}
                                        style={{
                                            ...(hasSupportAccess
                                                ? {}
                                                : { pointerEvents: 'none' })
                                        }}
                                    >
                                        <Typography
                                            as="span"
                                            sx={{
                                                ...(hasSupportAccess
                                                    ? { fontWeight: 600 }
                                                    : {})
                                            }}
                                        >
                                            {tenantName}
                                        </Typography>
                                    </Link>
                                    {brandName && (
                                        <small>
                                            <br />
                                            {name}
                                        </small>
                                    )}
                                </Box>
                            </DarkTooltip>
                        </Grid>
                        <Grid item xs={12}>
                            <small style={{ fontSize: '9px' }}>{id}</small>
                        </Grid>
                    </Grid>
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {region}
                </TableCell>
                {this.renderTenantStats(row.id)}
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    <DateFormat date={dateCreated} small noTz />
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {partner && (
                        <DateFormat
                            key={trialEndDate}
                            date={trialEndDate}
                            noTz
                            small
                        />
                    )}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {adminUser && adminUser.id && (
                        <>
                            <Box>
                                <Typography
                                    color="textSecondary"
                                    fontWeight={600}
                                >
                                    {adminUser.firstName} {adminUser.lastName}
                                </Typography>
                            </Box>
                            <Box>
                                <small>{adminUser.email}</small>
                            </Box>
                        </>
                    )}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }}>
                    {anm && partner && (
                        <Typography
                            color="text.tertiary"
                            sx={{ fontWeight: 600 }}
                        >
                            {t('GlobalAdmin.Tenant.created_via_anm')}
                        </Typography>
                    )}
                    {!partner && (
                        <Box width="100%">
                            <Link
                                className={
                                    hasSupportAccess ? 'link underline' : null
                                }
                                to={routeToPartner}
                                style={{
                                    ...(hasSupportAccess
                                        ? {}
                                        : { pointerEvents: 'none' })
                                }}
                            >
                                <Typography
                                    as="span"
                                    sx={{
                                        ...(hasSupportAccess
                                            ? { fontWeight: 600 }
                                            : {})
                                    }}
                                >
                                    {partnerTenant.name || t('Common.n/a')}
                                </Typography>
                            </Link>
                            <Typography sx={{ fontSize: '9px' }}>
                                {partnerTenant.id}
                            </Typography>
                        </Box>
                    )}
                </TableCell>
                <TableCell style={{ verticalAlign: 'baseline' }} align="right">
                    <Actions tenant={row} rerenderFn={() => this.rerender()} />
                </TableCell>
            </User>
        );
    };

    renderTable = () => {
        const { csvData, rerender } = this.state;

        return (
            <ManagedTable
                csvData={csvData}
                csvExport
                csvExportFields={csvFields}
                csvExportFileName="global_tenants"
                handleRows={this.handleRows}
                headers={headers}
                parameters={parameters}
                renderBody={this.renderTenant}
                renderBodyKey="id"
                renderFooter
                rerender={rerender}
                rowHeight={42}
                rowsApi="/tenant"
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="name"
                toggles={toggles}
            />
        );
    };

    render() {
        const { t } = this.props;
        const { loaded, adding } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Paper elevation={0} variant="outlined">
                <Box display="flex" flexDirection="column" width="100%">
                    <Box alignSelf="flex-end" mt="16px">
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() =>
                                this.setState({
                                    adding: true,
                                    adminEmail: '',
                                    partnerName: ''
                                })
                            }
                        >
                            <Add
                                style={{
                                    fontSize: FontSizes[1],
                                    marginRight: '4px'
                                }}
                            />
                            {t('GlobalAdmin.Tenant.createPartner')}
                        </Button>
                    </Box>
                    {this.renderTable()}

                    {adding && (
                        <Modal
                            open={adding}
                            TransitionComponent={Transition}
                            onClose={(_event, reason) => {
                                if (reason === 'backdropClick') {
                                    this.setState({ adding: false });
                                }
                            }}
                            title={t('GlobalAdmin.Tenant.createPartner')}
                        >
                            {this.renderCreatePartner()}
                        </Modal>
                    )}
                </Box>
            </Paper>
        );
    }
}

export default withRouter(withTranslation()(GlobalTenant));
